/**
 * Upload Page
 * Page for uploading audio files for transcription
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { MatterSelect } from '../components/echo/MatterSelect';
import { FileUpload } from '../components/echo/FileUpload';
import { WorkflowForm, WorkflowFormData } from '../components/echo/WorkflowForm';
import { WorkflowSuccessMessage } from '../components/echo/WorkflowSuccessMessage';
import { useS3Upload } from '../hooks/useS3Upload';
import { useWorkflowDispatch } from '../hooks/useWorkflowDispatch';
import { useEchoUser } from '../hooks/useEchoUser';
import { resolvePromptInputs } from '../config/echo';
import type { EchoMatter } from '../api/types/echo';
import type { WorkflowFormInput } from '../hooks/useWorkflowDispatch';

/**
 * Page component for uploading and transcribing audio files
 */
export function UploadPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useEchoUser();
  const s3Upload = useS3Upload();
  const workflow = useWorkflowDispatch();

  const [selectedMatter, setSelectedMatter] = useState<{
    id: number | null;
    matter: EchoMatter | null;
  }>({ id: null, matter: null });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleMatterChange = useCallback((matterId: number | null, matter: EchoMatter | null) => {
    setSelectedMatter({ id: matterId, matter });
  }, []);

  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
  }, []);

  const handleFormSubmit = useCallback(async (formData: WorkflowFormData) => {
    if (!selectedMatter.id || !user?.email || !selectedFile) {
      return;
    }

    // Step 1: Upload file to S3 using streamer name and VOD date
    const s3Url = await s3Upload.upload(selectedFile, formData.streamerName, formData.vodDate);
    if (!s3Url) {
      return; // Upload failed or was cancelled
    }

    // Resolve prompt inputs based on the selected prompt mode
    const { promptTemplate, flaggedTerms } = resolvePromptInputs({
      promptMode: formData.promptMode,
      customPrompt: formData.customPrompt,
      promptTemplate: formData.promptTemplate,
      flaggedTerms: formData.flaggedTerms,
      streamerName: formData.streamerName,
    });

    // Step 2: Dispatch workflow with S3 URL
    const workflowInput: WorkflowFormInput = {
      url: s3Url, // S3 URL used for uploaded files
      streamerName: formData.streamerName,
      date: formData.vodDate,
      promptTemplate,
      flaggedTerms,
      emailRecipients: formData.emailRecipients,
      language: formData.language,
    };

    // Use the matter ID as streamer ID for now (needs proper streamer selection)
    await workflow.dispatch(workflowInput, selectedMatter.id, user.email);
  }, [selectedMatter.id, user?.email, selectedFile, s3Upload, workflow]);

  const handleReset = useCallback(() => {
    s3Upload.reset();
    workflow.reset();
  }, [s3Upload, workflow]);

  const handleViewHistory = useCallback(() => {
    navigate('/history');
  }, [navigate]);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  // Determine overall state
  const isProcessing = s3Upload.isUploading || workflow.isDispatching || workflow.isPolling;
  const canSubmit = !!selectedMatter.id && !!selectedFile && !isProcessing;

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          Upload, Transcribe, and Summarize
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          Upload an audio file to start transcription and summarization.
        </p>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          Contact the engineering team if assistance is needed in finding an mp3.
        </p>
      </div>

      {/* Main Form Card */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="lg">
            {/* Matter Selection */}
            <MatterSelect
              value={selectedMatter.id}
              onChange={handleMatterChange}
              required
              label="Select Matter"
              placeholder="Choose a matter for billing..."
              showBillingRate={false}
            />

            {/* File Upload */}
            <Stack gap="xs">
              <label style={{ ...textStyles.label, color: textColor }}>
                Audio File <span style={{ color: theme.colors.semantic.error.default }}>*</span>
              </label>
              <FileUpload
                onFileSelect={handleFileSelect}
                accept=".mp3,audio/mpeg"
                maxSizeMB={500}
                disabled={isProcessing}
                uploadProgress={s3Upload.progress}
                isUploading={s3Upload.isUploading}
              />
            </Stack>

            {/* Workflow Form (without URL field) */}
            <WorkflowForm
              onSubmit={handleFormSubmit}
              isSubmitting={isProcessing}
              defaultPrompt=""
              showUrlField={false}
            />

            {/* Validation warnings */}
            {!selectedMatter.id && (
              <div
                style={{
                  padding: spacing.sm,
                  background: `${theme.colors.semantic.warning.light}20`,
                  border: `1px solid ${theme.colors.semantic.warning.light}`,
                  borderRadius: '6px',
                  fontSize: fontSize.sm,
                  color: theme.colors.semantic.warning.default,
                }}
              >
                Please select a matter before submitting.
              </div>
            )}

            {selectedMatter.id && !selectedFile && (
              <div
                style={{
                  padding: spacing.sm,
                  background: `${theme.colors.semantic.warning.light}20`,
                  border: `1px solid ${theme.colors.semantic.warning.light}`,
                  borderRadius: '6px',
                  fontSize: fontSize.sm,
                  color: theme.colors.semantic.warning.default,
                }}
              >
                Please select an audio file to upload.
              </div>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* S3 Upload Error */}
      {s3Upload.error && (
        <Card variant="outlined">
          <Card.Body>
            <Stack gap="sm">
              <h3 style={{ margin: 0, color: theme.colors.semantic.error.default, ...textStyles.h6 }}>
                Upload Error
              </h3>
              <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
                {s3Upload.error}
              </p>
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* Workflow Status */}
      {(workflow.isDispatching || workflow.isPolling || workflow.run || workflow.error) && (
        <WorkflowSuccessMessage
          isDispatching={(workflow.isDispatching || workflow.isPolling) && !workflow.run}
          isSuccess={!!workflow.run && !workflow.error}
          error={workflow.error}
          onReset={handleReset}
          onViewHistory={handleViewHistory}
        />
      )}

      {/* Help Section */}
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, ...textStyles.bodySmall, fontWeight: textStyles.label.fontWeight, color: textColor }}>
              Supported File Types
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                color: mutedColor,
                fontSize: fontSize.sm,
              }}
            >
              <li>MP3 audio files (.mp3)</li>
              <li>Maximum file size: 500MB</li>
            </ul>
            <p style={{ margin: 0, marginTop: spacing.sm, color: mutedColor, fontSize: fontSize.sm }}>
              After upload, the file will be processed through the transcription workflow.
              You can track the progress and view results once complete.
            </p>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default UploadPage;
